/**
 * Patients GraphQL Service
 * Consumes patient-related GraphQL queries from resources/core
 */

import { graphqlClient } from '@/services/core/graphqlClient';
import { PATIENT_QUERIES } from '@/services/resources';

export interface PatientData {
    patient_clinic_data_id: number;
    first_name: string;
    last_name: string;
    full_name: string;
    dob: string;
    gender: string;
    mobile_phone_number?: string;
    home_phone_number?: string;
    email?: string;
    address_line_1?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    clinic_name: string;
    provider_id: number;
    provider_name: string;
    primary_dx?: string;
    secondary_dx?: string;
    primary_insurance_provider?: string;
    secondary_insurance_provider?: string;
    emr_id?: string;
    ccm_status?: number;
    rpm_status?: number;
    patient_clinic_timezone?: string;
    rpm_appointment_facility?: number;
}

export interface ConsolidatedPatientData {
    patient_clinic_data_id: number;
    full_name: string;
    dob: string;
    emr_id?: string;
    provider_name: string;
    primary_dx?: string;
    secondary_dx?: string;
    clinic_id: number;
    facility_id?: number;
    provider_id?: number;
    ccm_status?: number;
    rpm_status?: number;
    has_active_group?: boolean;
    search_string?: string;
    mobile_phone_number?: string;
    home_phone_number?: string;
    primary_insurance_provider?: string;
    secondary_insurance_provider?: string;
    insurance_type?: string;
    last_seen_date?: string;
    pcm_flag?: boolean;
    ccm_flag?: boolean;
    rpm_flag?: boolean;
}

export class PatientsGraphqlService {
    /**
     * Get consolidated patient data with filtering and pagination
     */
    async getConsolidatedData(
        hasuraToken: string,
        where?: any,
        orderBy?: any[],
        limit?: number,
        offset?: number
    ) {
        return await graphqlClient.query<{ pep_vw_consolidated_data: ConsolidatedPatientData[] }>(
            PATIENT_QUERIES.GET_CONSOLIDATED_DATA,
            { where, orderBy, limit, offset },
            hasuraToken
        );
    }

    /**
     * Get detailed patient data by ID
     */
    async getPatientData(hasuraToken: string, patientId: number) {
        return await graphqlClient.query<{ pep_vw_consolidated_data: PatientData[] }>(
            PATIENT_QUERIES.GET_PATIENT_DATA,
            { patientId },
            hasuraToken
        );
    }

    /**
     * Get patient consolidated data by ID
     */
    async getPatientConsolidatedData(hasuraToken: string, patientId: number) {
        return await graphqlClient.query<{ pep_vw_consolidated_data: PatientData[] }>(
            PATIENT_QUERIES.GET_PATIENT_CONSOLIDATED_DATA,
            { patientId },
            hasuraToken
        );
    }

    /**
     * Get patient details by ID
     */
    async getPatientDetails(hasuraToken: string, patientClinicDataId: number) {
      return await graphqlClient.query<{ pep_vw_consolidated_data: PatientData[] }>(
        PATIENT_QUERIES.GET_PATIENT_DETAILS,
        { patientClinicDataId },
        hasuraToken
      );
    }

    /**
     * Get care specialist patient list
     */
    async getCareSpecialistPatientList(hasuraToken: string, clinicIds: number[]) {
        return await graphqlClient.query(
            PATIENT_QUERIES.GET_CARE_SPECIALIST_PATIENT_LIST,
            { clinicIds },
            hasuraToken
        );
    }

    /**
     * Get patient enroller patient list
     */
    async getPatientEnrollerPatientList(hasuraToken: string, clinicIds: number[]) {
        return await graphqlClient.query(
            PATIENT_QUERIES.GET_PE_PATIENT_LIST,
            { clinicIds },
            hasuraToken
        );
    }

    /**
     * Get care specialist consent patients
     */
    async getCareSpecialistConsentPatients(hasuraToken: string, clinicIds: number[]) {
        return await graphqlClient.query(
            PATIENT_QUERIES.GET_CS_CONSENT_PATIENTS,
            { clinicIds },
            hasuraToken
        );
    }

    /**
     * Get care specialist follow-up patients
     */
    async getCareSpecialistFollowUpPatients(hasuraToken: string) {
        return await graphqlClient.query(
            PATIENT_QUERIES.GET_CS_FOLLOWUP_PATIENTS,
            {},
            hasuraToken
        );
    }

    /**
     * Get care specialist deferred patients
     */
    async getCareSpecialistDeferredPatients(hasuraToken: string) {
        return await graphqlClient.query(
            PATIENT_QUERIES.GET_CS_DEFERRED_PATIENTS,
            {},
            hasuraToken
        );
    }

    /**
     * Get care specialist task patient list
     */
    async getCareSpecialistTaskPatientList(hasuraToken: string, whereClause: string) {
        return await graphqlClient.query(
            PATIENT_QUERIES.GET_CS_TASK_PATIENT_LIST,
            { whereClause },
            hasuraToken
        );
    }

    /**
     * Get patient enroller task patient list
     */
    async getPatientEnrollerTaskPatientList(
        hasuraToken: string,
        clinicIds: number[],
        statusFilter: string,
        whereConditions?: string
    ) {
        return await graphqlClient.query(
            PATIENT_QUERIES.GET_PE_TASK_PATIENT_LIST,
            { clinicIds, statusFilter },
            hasuraToken
        );
    }

    /**
     * Get SMS patient data
     */
    async getSMSPatientData(hasuraToken: string, patientId: number) {
        return await graphqlClient.query(
            PATIENT_QUERIES.GET_SMS_PATIENT_DATA,
            { patientId },
            hasuraToken
        );
    }

    /**
     * Get ES patient data using assigned_user_id from workflow assignments view
     */
    async getESPatientData(hasuraToken: string, userId: number) {
        return await graphqlClient.query<{ 
            pep_vw_patient_workflow_user_assignments: any[];
            pep_vw_patient_workflow_user_assignments_aggregate: { aggregate: { count: number } };
        }>(
            PATIENT_QUERIES.ES_PATIENT_DATA,
            { userId },
            hasuraToken
        );
    }

    /**
     * Get patient assignment by userId from workflow assignments view
     * Used when navigating to VPE from external pages (PE/CS dashboard, global search)
     */
    async getPatientAssignmentByPatientId(hasuraToken: string, userId: number) {
        return await graphqlClient.query<{
            pep_vw_patient_workflow_user_assignments: any[];
        }>(
            PATIENT_QUERIES.PATIENT_ASSIGNMENT_BY_PATIENT_ID,
            { userId },
            hasuraToken
        );
    }

    /**
     * Get patients by EMR IDs
     */
    async getPatientsByEmrIds(hasuraToken: string, emrIds: string[], clinicId: number) {
        return await graphqlClient.query(
            PATIENT_QUERIES.GET_PATIENTS_BY_EMR_IDS,
            { emrIds, clinicId },
            hasuraToken
        );
    }

    /**
     * Get patient details for group creation (patients without groups)
     */
    async getPatientDetailsForGroup(
        hasuraToken: string,
        offset: number = 0,
        limit: number = 10,
        where?: any
    ) {
        return await graphqlClient.query(
            PATIENT_QUERIES.GET_PATIENT_DETAILS_FOR_GROUP,
            { offset, limit, where },
            hasuraToken
        );
    }

    /**
     * Get patients by EMR list with aggregate count
     */
    async getPatientsByEmrList(
        hasuraToken: string,
        clinicId: number,
        emrIds: string[],
        limit?: number,
        offset?: number
    ) {
        return await graphqlClient.query(
            PATIENT_QUERIES.GET_PATIENTS_BY_EMR_LIST,
            { clinicId, emrIds, limit, offset },
            hasuraToken
        );
    }
}

// Export singleton instance
export const patientsGraphqlService = new PatientsGraphqlService();
export default patientsGraphqlService;